import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Data service
import { 
  getECMLicense, 
  createECMLicense, 
  updateECMLicense 
} from "services/ecmLicenseService";

function ECMLicenseForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    installname: '',
    installhostname: '',
    edition: '',
    usernumber: '',
    userid: 1 // Default user ID, can be changed as needed
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "info",
  });

  const editionOptions = [
    { value: 1, label: "Standard" },
    { value: 2, label: "Professional" },
    { value: 3, label: "Enterprise" },
    { value: 4, label: "Cloud" },
  ];

  useEffect(() => {
    if (isEditMode) {
      fetchLicenseData();
    }
  }, [id]);
  
  const fetchLicenseData = async () => {
    try {
      setIsLoading(true);
      const licenseData = await getECMLicense(id);
      
      setFormData({
        installname: licenseData.installname,
        installhostname: licenseData.installhostname,
        edition: licenseData.edition,
        usernumber: licenseData.usernumber,
        userid: licenseData.userid || 1
      });
    } catch (error) {
      console.error("Failed to fetch license data:", error);
      setSnackbar({
        open: true,
        message: "Failed to load license data. Please try again.",
        color: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name) => (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption.value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      // Convert usernumber to integer
      const dataToSubmit = {
        ...formData,
        usernumber: parseInt(formData.usernumber, 10),
        edition: parseInt(formData.edition, 10)
      };
      
      if (isEditMode) {
        await updateECMLicense(id, dataToSubmit);
      } else {
        await createECMLicense(dataToSubmit);
      }
      
      setSnackbar({
        open: true,
        message: `License ${isEditMode ? 'updated' : 'created'} successfully!`,
        color: "success",
      });
      
      // Wait a moment before navigating away
      setTimeout(() => {
        navigate("/services/ecm");
      }, 1500);
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} license:`, error);
      setSnackbar({
        open: true,
        message: `Failed to ${isEditMode ? 'update' : 'create'} license. Please try again.`,
        color: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="h5">
                {isEditMode ? 'Edit' : 'Add'} ECM License
              </SoftTypography>
              <SoftBox mt={2}>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <SoftBox mb={2}>
                        <SoftInput
                          name="installname"
                          placeholder="License Name"
                          value={formData.installname}
                          onChange={handleChange}
                          required
                          fullWidth
                        />
                      </SoftBox>
                      <SoftBox mb={2}>
                        <SoftInput
                          name="installhostname"
                          placeholder="Hostname"
                          value={formData.installhostname}
                          onChange={handleChange}
                          required
                          fullWidth
                        />
                      </SoftBox>
                      <SoftBox mb={2}>
                        <SoftSelect
                          name="edition"
                          placeholder="Edition"
                          value={editionOptions.find(option => option.value === parseInt(formData.edition, 10))}
                          options={editionOptions}
                          onChange={handleSelectChange('edition')}
                          isRequired
                        />
                      </SoftBox>
                      <SoftBox mb={2}>
                        <SoftInput
                          name="usernumber"
                          type="number"
                          placeholder="Number of Users"
                          value={formData.usernumber}
                          onChange={handleChange}
                          required
                          fullWidth
                        />
                      </SoftBox>
                    </Grid>
                  </Grid>
                  <SoftBox mt={3} display="flex" justifyContent="flex-end">
                    <SoftButton
                      type="button"
                      variant="text"
                      color="secondary"
                      onClick={() => navigate("/services/ecm")}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </SoftButton>
                    <SoftButton
                      type="submit"
                      variant="gradient"
                      color="info"
                      disabled={isLoading}
                    >
                      {isLoading ? "Processing..." : isEditMode ? "Update License" : "Create License"}
                    </SoftButton>
                  </SoftBox>
                </form>
              </SoftBox>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
      <Footer />
      <SoftSnackbar
        color={snackbar.color}
        icon={snackbar.color === "success" ? "check" : "warning"}
        title={snackbar.color === "success" ? "Success" : "Alert"}
        content={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
        close={closeSnackbar}
      />
    </DashboardLayout>
  );
}

export default ECMLicenseForm;